import {useState, useRef, useCallback, useMemo} from 'react';
import {useTheme} from '../contexts/ThemeContext';
import {FiSun, FiMoon} from 'react-icons/fi';
import {HiOutlineDesktopComputer} from "react-icons/hi";
import {useClickOutside} from '../hooks/useClickOutside';
import type {Theme, ThemeOption, ThemeToggleProps} from '../types/theme.types';

export default function ThemeToggle({className = ''}: ThemeToggleProps) {
    const {theme, setTheme} = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const closeDropdown = useCallback(() => setIsOpen(false), []);

    useClickOutside(dropdownRef, closeDropdown);

    const THEME_OPTIONS = useMemo<ThemeOption[]>(() => [
        {value: 'light', label: 'Light', icon: <FiSun className="w-4 h-4"/>},
        {value: 'dark', label: 'Dark', icon: <FiMoon className="w-4 h-4"/>},
        {value: 'system', label: 'System', icon: <HiOutlineDesktopComputer className="w-4 h-4"/>},
    ], []);

    const activeThemeIcon = THEME_OPTIONS.find(opt => opt.value === theme)?.icon || <FiSun/>;

    const handleThemeSelect = (selectedTheme: Theme) => {
        setTheme(selectedTheme);
        closeDropdown();
    };

    return (
        <div ref={dropdownRef} className={`relative inline-block text-left ${className}`}>
            <button
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-label="Tema seçici menüsünü aç"
                className="
                    flex items-center justify-center w-10 h-10 rounded-full
                    transition-colors duration-200 ease-in-out
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                    dark:focus-visible:ring-offset-gray-900
                    hover:bg-gray-200 dark:hover:bg-gray-800
                    text-gray-600 dark:text-gray-300
                "
            >
                {activeThemeIcon}
            </button>

            {isOpen && (
                <div
                    role="menu"
                    aria-orientation="vertical"
                    className="
                        absolute right-0 z-50 w-36 mt-2 origin-top-right
                        bg-white dark:bg-gray-900
                        border border-gray-200 dark:border-gray-800
                        rounded-md shadow-lg ring-1 ring-black ring-opacity-5
                        focus:outline-none animate-in fade-in zoom-in-95 duration-100
                    "
                >
                    <div className="py-1" role="none">
                        {THEME_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handleThemeSelect(option.value)}
                                role="menuitem"
                                className={`
                                    flex items-center gap-3 w-full px-4 py-2 text-sm text-left
                                    transition-colors duration-150
                                    ${theme === option.value
                                    ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/10'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }
                                `}
                            >
                                {option.icon}
                                <span className="font-medium">{option.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}